/**
 * Content Routes Configuration
 *
 * Defines all application routes and their corresponding page components.
 * Protected routes are wrapped with ProtectedRoute to enforce authentication.
 * Only the login and 404 pages are accessible without authentication.
 *
 * SECURITY FIX: All non-auth routes are now wrapped with ProtectedRoute
 * to prevent unauthorized access by direct URL navigation.
 */
import React, { lazy } from 'react';
import { RouteProps } from 'react-router-dom';
import { loginPages, dashboardPagesMenu, masterPagesMenu, superAdminMenu } from '../menu';
import Login from '../pages/presentation/auth/Login';
import ProtectedRoute from '../components/ProtectedRoute';
import ManagementLeaveReport from '../pages/ReportsPage/ManagementLeaveReport';
import DesignationWiseLeaveReport from '../pages/ReportsPage/DesignationWiseLeaveReport';
import MonthlyAttendanceSummaryReport from '../pages/ReportsPage/MonthlyAttendanceSummaryReport';
import EmployeeLeaveReport from '../pages/ReportsPage/EmployeeLeaveReport';
import MonthlyCheckoutReport from '../pages/ReportsPage/MonthlyCheckoutReport';
import CompOffReportPage from '../pages/ReportsPage/CompOffReportPage';
import OnDutyReportPage from '../pages/ReportsPage/OnDutyReportPage';
import FestivalAdvancesReportPage from '../pages/ReportsPage/FestivalAdvancesReportPage';
import MedicalAllowanceReportPage from '../pages/ReportsPage/MedicalAllowanceReportPage';
import PLSurrenderReportPage from '../pages/ReportsPage/PLSurrenderReportPage';

const LANDING = {
	DASHBOARD: lazy(() => import('../pages/presentation/dashboard/DashboardPage')),
};
const AUTH = {
	PAGE_404: lazy(() => import('../pages/presentation/auth/Page404')),
};
const MASTER = {
	BRANCH: lazy(() => import('../pages/branch/branchPage')),
	SECTION: lazy(() => import('../pages/section/sectionPage')),
	EMPLOYEE: lazy(() => import('../pages/employee/employeePage')),
	TRANSFER: lazy(() => import('../pages/UpdateEmployee/TransferEmployeeComponent')),
	WEB_CHECKIN: lazy(() => import('../pages/webCheckIn/webCheckInComponent')),
	VISITOR_PASS: lazy(() => import('../pages/VisitorPass/VisitorPassComponent')),
	ORGANISATION: lazy(() => import('../pages/orgnisation/organisationPage')),
	REPORTS: lazy(() => import('../pages/ReportsPage/ReportsPage')),
	LEAVE_REPORT: lazy(() => import('../pages/ReportsPage/LeaveReportPage')),
	SECTION_WISE_ATTENDANCE: lazy(() => import('../pages/ReportsPage/SectionWiseAttendanceReport')),
	CHECK_IN_LOCKED: lazy(() => import('../pages/CheckInLocked/CheckInLockedReport')),
	LEAVE_REASON_MASTER: lazy(() => import('../pages/LeaveReasonMaster/LeaveReasonsAdd')),
	LEAVE_RESTRICTION: lazy(() => import('../pages/LeaveRestrictionPage/LeaveRestrictionPage')),
	APPLY_LEAVE_ADMIN: lazy(() => import('../pages/ApplyLeaveAdmin/ApplyLeaveAdminPage')),
	LEAVE_ADJUSTMENT: lazy(() => import('../pages/LeaveAdjustment/LeaveAdjustmentPage')),
	REQUEST_COMPOFF_ADMIN: lazy(
		() => import('../pages/RequestCompOffAdmin/RequestCompOffAdminPage'),
	),
	REFRESHMENTS: lazy(() => import('../pages/Allowances/RefreshmentsPage')),
	NEWSPAPER: lazy(() => import('../pages/Allowances/NewspaperPage')),
};
const presentation: RouteProps[] = [
	/**
	 * Landing - Dashboard (Protected: requires authentication)
	 */
	{
		path: dashboardPagesMenu.dashboard.path,
		element: (
			<ProtectedRoute>
				<LANDING.DASHBOARD />
			</ProtectedRoute>
		),
	},

	/** ************************************************** */

	/** ********************* PUBLIC AUTH ROUTES (No protection) ***************************** */
	{
		path: loginPages.page404.path,
		element: <AUTH.PAGE_404 />,
	},
	{
		path: loginPages.login.path,
		element: <Login />,
	},
	/** ********************* LOGIN END ***************************** */

	/**
	 * App > Master Pages (All protected routes)
	 */
	{
		path: masterPagesMenu.master.subMenu.branchList.path,
		element: (
			<ProtectedRoute>
				<MASTER.BRANCH />
			</ProtectedRoute>
		),
	},
	{
		path: masterPagesMenu.master.subMenu.sectionList.path,
		element: (
			<ProtectedRoute>
				<MASTER.SECTION />
			</ProtectedRoute>
		),
	},
	{
		path: masterPagesMenu.master.subMenu.employeeList.path,
		element: (
			<ProtectedRoute>
				<MASTER.EMPLOYEE />
			</ProtectedRoute>
		),
	},
	{
		path: masterPagesMenu.master.subMenu.transferList.path,
		element: (
			<ProtectedRoute>
				<MASTER.TRANSFER />
			</ProtectedRoute>
		),
	},
	{
		path: masterPagesMenu.master.subMenu.webCheckIn.path,
		element: (
			<ProtectedRoute>
				<MASTER.WEB_CHECKIN />
			</ProtectedRoute>
		),
	},
	{
		path: masterPagesMenu.master.subMenu.visitorPass.path,
		element: (
			<ProtectedRoute>
				<MASTER.VISITOR_PASS />
			</ProtectedRoute>
		),
	},
	{
		path: masterPagesMenu.master.subMenu.leaveRestriction.path,
		element: (
			<ProtectedRoute>
				<MASTER.LEAVE_RESTRICTION />
			</ProtectedRoute>
		),
	},
	{
		path: masterPagesMenu.master.subMenu.applyLeaveAdmin.path,
		element: (
			<ProtectedRoute>
				<MASTER.APPLY_LEAVE_ADMIN />
			</ProtectedRoute>
		),
	},
	{
		path: masterPagesMenu.master.subMenu.leaveAdjustment.path,
		element: (
			<ProtectedRoute>
				<MASTER.LEAVE_ADJUSTMENT />
			</ProtectedRoute>
		),
	},
	{
		path: masterPagesMenu.master.subMenu.requestCompOffAdmin.path,
		element: (
			<ProtectedRoute>
				<MASTER.REQUEST_COMPOFF_ADMIN />
			</ProtectedRoute>
		),
	},
	{
		path: masterPagesMenu.master.subMenu.leaveReasonMaster.path,
		element: (
			<ProtectedRoute>
				<MASTER.LEAVE_REASON_MASTER />
			</ProtectedRoute>
		),
	},
	{
		path: superAdminMenu.master.subMenu.organisationList.path,
		element: (
			<ProtectedRoute>
				<MASTER.ORGANISATION />
			</ProtectedRoute>
		),
	},
	{
		path: 'reports/xreports-generate',
		element: (
			<ProtectedRoute>
				<MASTER.REPORTS />
			</ProtectedRoute>
		),
	},
	{
		path: 'reports/attendance-report',
		element: (
			<ProtectedRoute>
				<MASTER.REPORTS />
			</ProtectedRoute>
		),
	},
	{
		path: 'reports/leave-report',
		element: (
			<ProtectedRoute>
				<React.Suspense fallback={<div>Loading...</div>}>
					<MASTER.LEAVE_REPORT />
				</React.Suspense>
			</ProtectedRoute>
		),
	},
	{
		path: 'reports/section-wise-attendance-report',
		element: (
			<ProtectedRoute>
				<MASTER.SECTION_WISE_ATTENDANCE />
			</ProtectedRoute>
		),
	},
	{
		path: 'reports/check-in-locked',
		element: (
			<ProtectedRoute>
				<MASTER.CHECK_IN_LOCKED />
			</ProtectedRoute>
		),
	},
	{
		path: 'allowances/refreshments',
		element: (
			<ProtectedRoute>
				<MASTER.REFRESHMENTS />
			</ProtectedRoute>
		),
	},
	{
		path: 'allowances/newspaper',
		element: (
			<ProtectedRoute>
				<MASTER.NEWSPAPER />
			</ProtectedRoute>
		),
	},

	/**
	 * END - App
	 */

	/** ************************************************** */
];

/** Report routes - all protected */
const leaveReportRoutes: RouteProps[] = [
	{
		path: 'reports/management-leave',
		element: (
			<ProtectedRoute>
				<ManagementLeaveReport />
			</ProtectedRoute>
		),
	},
	{
		path: 'reports/designation-leave',
		element: (
			<ProtectedRoute>
				<DesignationWiseLeaveReport />
			</ProtectedRoute>
		),
	},
	{
		path: 'reports/monthly-attendance-summary',
		element: (
			<ProtectedRoute>
				<MonthlyAttendanceSummaryReport />
			</ProtectedRoute>
		),
	},
	{
		path: 'reports/employee-leave',
		element: (
			<ProtectedRoute>
				<EmployeeLeaveReport />
			</ProtectedRoute>
		),
	},
	{
		path: 'reports/monthly-checkout',
		element: (
			<ProtectedRoute>
				<MonthlyCheckoutReport />
			</ProtectedRoute>
		),
	},
	{
		path: 'reports/compoff',
		element: (
			<ProtectedRoute>
				<CompOffReportPage />
			</ProtectedRoute>
		),
	},
	{
		path: 'reports/on-duty',
		element: (
			<ProtectedRoute>
				<OnDutyReportPage />
			</ProtectedRoute>
		),
	},
	{
		path: 'reports/festival-advances',
		element: (
			<ProtectedRoute>
				<FestivalAdvancesReportPage />
			</ProtectedRoute>
		),
	},
	{
		path: 'reports/medical-allowance',
		element: (
			<ProtectedRoute>
				<MedicalAllowanceReportPage />
			</ProtectedRoute>
		),
	},
	{
		path: 'reports/pl-surrender',
		element: (
			<ProtectedRoute>
				<PLSurrenderReportPage />
			</ProtectedRoute>
		),
	},
];

const contentRoutes: RouteProps[] = [...presentation, ...leaveReportRoutes];

export default contentRoutes;
